import { useEffect, useRef, useState } from 'react';
import { D, fiscalDate, formatMoney, normalizeNuit, NUIT_RE } from '@makila/fiscal';
import {
  advanceFromDocument, cancelReservation, collectReservation, payReservation, reservationOverdue, reservationPaid, reservationRest,
  type Customer, type IssuedDocument, type LicenceStatus, type Reservation, type Signer, type Tender,
} from '@makila/pos-core';
import { Bookmark, X } from 'lucide-react';
import clsx from 'clsx';
import { ManagerApproval } from './ManagerApproval';
import { PayDialog } from './PayDialog';
import { type DeviceState, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';

const addDays = (n: number) => new Date(Date.parse(`${fiscalDate()}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const dmy = (d: string) => d.split('-').reverse().join('/');
const amountOf = (v: string) => (/^\d{1,9}([.,]\d{1,2})?$/.test(v.trim()) ? new D(v.trim().replace(',', '.')) : null);

function Shell(p: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className={clsx('m-auto rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]', p.wide ? 'w-[min(760px,96vw)]' : 'w-[min(560px,96vw)]')}>
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="flex items-center gap-2 text-xl font-bold"><Bookmark size={22} className="text-brand-700" /> {p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="max-h-[80vh] overflow-y-auto p-5">{p.children}</div>
    </dialog>
  );
}

/**
 * Reservar o carrinho (decisão F4): cliente com NUIT, prazo para levantar e
 * sinal. Segue-se o pagamento do sinal (factura-recibo de adiantamento).
 */
export function ReserveDialog(p: { total: string; customer: Customer | null; onClose: () => void; onNext: (r: { customer: { id?: string; name: string; nuit: string; phone: string | null }; dueDate: string; deposit: string }) => void }) {
  const [name, setName] = useState(p.customer?.name ?? '');
  const [nuit, setNuit] = useState(p.customer?.nuit ?? '');
  const [phone, setPhone] = useState('');
  const [due, setDue] = useState(addDays(15));
  const half = new D(p.total).div(2).toDecimalPlaces(0);
  const [deposit, setDeposit] = useState(half.toString());
  const n = amountOf(deposit);
  const nuitOk = NUIT_RE.test(normalizeNuit(nuit) ?? '');
  const ok = name.trim() && nuitOk && n?.greaterThan(0) && n.lessThanOrEqualTo(p.total) && due >= fiscalDate();
  const presets = [30, 50, 100].map((pc) => ({ pc, v: pc === 100 ? new D(p.total) : new D(p.total).times(pc).div(100).toDecimalPlaces(0) }));
  const field = 'h-12 w-full rounded-xl border border-base bg-surface-2 px-4';
  return (
    <Shell title="Reservar com sinal" onClose={p.onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault();
        if (ok) p.onNext({ customer: { ...(p.customer?.id && p.customer.name === name.trim() ? { id: p.customer.id } : {}), name: name.trim(), nuit: normalizeNuit(nuit)!, phone: phone.trim() || null }, dueDate: due, deposit: n!.toFixed(2) });
      }}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className="text-sm font-semibold">Cliente</span><input aria-label="Nome do cliente" value={name} onChange={(e) => setName(e.target.value)} className={clsx(field, 'mt-1')} /></label>
          <label className="block"><span className="text-sm font-semibold">NUIT</span><input aria-label="NUIT do cliente" inputMode="numeric" maxLength={11} value={nuit} onChange={(e) => setNuit(e.target.value)} className={clsx(field, 'mt-1 num')} /></label>
          <label className="block"><span className="text-sm font-semibold">Telefone</span><input aria-label="Telefone do cliente" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={clsx(field, 'mt-1')} /></label>
          <label className="block"><span className="text-sm font-semibold">Levantar até</span><input aria-label="Levantar até" type="date" min={fiscalDate()} value={due} onChange={(e) => setDue(e.target.value)} className={clsx(field, 'mt-1')} /></label>
        </div>
        {nuit && !nuitOk && <p className="text-sm text-brand-700">NUIT: 9 algarismos.</p>}
        <div>
          <div className="mb-2 flex items-baseline justify-between text-sm"><span className="font-semibold">Sinal</span><span className="text-muted">Total <span className="num">{formatMoney(p.total)}</span> MT</span></div>
          <div className="grid grid-cols-3 gap-2">
            {presets.map((x) => <button key={x.pc} type="button" aria-pressed={n?.equals(x.v)} onClick={() => setDeposit(x.v.toString())} className={clsx('h-12 rounded-xl border-2 font-semibold', n?.equals(x.v) ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{x.pc === 100 ? 'Tudo' : `${x.pc}%`}</button>)}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input inputMode="decimal" aria-label="Valor do sinal" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="num h-14 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-2xl" />
            <span className="text-xl font-bold">MT</span>
          </div>
        </div>
        <p className="text-sm text-muted">O sinal sai numa factura-recibo de adiantamento, com IVA. No levantamento, a factura das peças abate o que foi pago. As peças ficam guardadas e deixam de estar disponíveis para venda.</p>
        <button disabled={!ok} className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Continuar para o pagamento do sinal</button>
      </form>
    </Shell>
  );
}

type Mode = { kind: 'pay'; r: Reservation; amount: string; step: 'amount' | 'pay' } | { kind: 'collect'; r: Reservation } | { kind: 'cancel'; r: Reservation; approver: Operator | null; reason: string; refund: 'NU' | 'MP' | 'none'; ref: string };

/** Reservas desta loja: pagar mais, levantar (e cobrar o resto) ou anular (gerente). */
export function ReservationsDialog(p: {
  store: IdbTerminalStore; device: DeviceState; signer: Signer; operator: Operator; cashSessionId: string; licence: LicenceStatus;
  onClose: () => void; onDone: (docs: IssuedDocument[], message: string) => void;
}) {
  const [list, setList] = useState<Reservation[] | null>(null);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void p.store.catalog('reservation').then((items) => setList(items.map((i) => i.data as unknown as Reservation)
      .filter((r) => r.status === 'open' && (!r.storeId || r.storeId === p.device.store.id)).sort((a, b) => a.dueDate.localeCompare(b.dueDate))));
  }, [p.store, p.device.store.id]);
  const who = identity(p.device);
  /** A caixa fica logo a saber (a nuvem confirma na sincronização). */
  const keep = async (r: Reservation) => p.store.applyCatalog([{ type: 'reservation', id: r.id, version: 0, data: r as unknown as Record<string, unknown> }], await p.store.catalogCursor());
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  if (mode?.kind === 'pay' && mode.step === 'pay') {
    return <PayDialog total={mode.amount} hint={`Reserva ${mode.r.code} · ${mode.r.customer.name}`} onClose={() => setMode({ ...mode, step: 'amount' })}
      onConfirm={async (tenders) => {
        const doc = await payReservation(p.store, who, p.signer, { reservation: mode.r, amount: mode.amount, tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence });
        await keep({ ...mode.r, advances: [...mode.r.advances, advanceFromDocument(doc)] });
        p.onDone([doc], `Reserva ${mode.r.code}: recebido ${formatMoney(doc.grossTotal)} MT`);
      }} />;
  }
  if (mode?.kind === 'collect' && new D(reservationRest(mode.r)).greaterThan(0)) {
    return <PayDialog total={reservationRest(mode.r)} hint={`Levantamento da reserva ${mode.r.code} · já pago ${formatMoney(reservationPaid(mode.r))} MT`} onClose={() => setMode(null)}
      onConfirm={async (tenders: Tender[]) => {
        const r = await collectReservation(p.store, who, p.signer, { reservation: mode.r, tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence });
        await keep({ ...mode.r, status: 'collected' });
        p.onDone([r.document, ...r.creditNotes], `Reserva ${mode.r.code} levantada`);
      }} />;
  }

  const s = q.trim().toLowerCase();
  const shown = (list ?? []).filter((r) => !s || r.code.toLowerCase().includes(s) || r.customer.name.toLowerCase().includes(s) || (r.customer.phone ?? '').includes(s));
  return (
    <Shell title={mode ? `Reserva ${mode.r.code}` : 'Reservas'} onClose={mode ? () => setMode(null) : p.onClose} wide>
      {error && <p role="alert" className="mb-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      {!mode && (
        <>
          <input type="search" aria-label="Procurar reserva" placeholder="Código, nome ou telefone" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
          {list && !shown.length && <p className="py-8 text-center text-muted">{list.length ? 'Nenhuma reserva com essa procura.' : 'Não há reservas por levantar nesta loja.'}</p>}
          <ul className="space-y-2">
            {shown.map((r) => {
              const late = reservationOverdue(r);
              return (
                <li key={r.id} className="surface rounded-xl p-3">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-mono text-lg font-bold tracking-wider">{r.code}</span>
                    <span className="font-semibold">{r.customer.name}</span>
                    {r.customer.phone && <span className="text-sm text-muted">{r.customer.phone}</span>}
                    <span className={clsx('ml-auto text-sm font-medium', late ? 'text-brand-700' : 'text-muted')}>{late ? 'Fora de prazo · ' : ''}até {dmy(r.dueDate)}</span>
                  </div>
                  <div className="mt-1 text-sm text-muted">{r.items.map((i) => `${new D(i.quantity).toString().replace('.', ',')} × ${i.description}`).join(' · ')}</div>
                  <div className="num mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    <span>Total <b>{formatMoney(r.total)}</b></span><span>Pago <b>{formatMoney(reservationPaid(r))}</b></span>
                    <span className="text-brand-700">Falta <b>{formatMoney(reservationRest(r))}</b></span>
                    <span className="ml-auto flex gap-2">
                      <button onClick={() => setMode({ kind: 'cancel', r, approver: null, reason: '', refund: 'NU', ref: '' })} className="h-11 rounded-lg border border-base px-3 font-medium">Anular</button>
                      {new D(reservationRest(r)).greaterThan(0) && <button onClick={() => setMode({ kind: 'pay', r, amount: reservationRest(r), step: 'amount' })} className="h-11 rounded-lg border border-base px-3 font-medium">Pagar mais</button>}
                      <button onClick={() => setMode({ kind: 'collect', r })} className="h-11 rounded-lg bg-brand-700 px-4 font-bold text-white">Levantar</button>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {mode?.kind === 'pay' && (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); const n = amountOf(mode.amount); if (n?.greaterThan(0)) setMode({ ...mode, amount: n.toFixed(2), step: 'pay' }); }}>
          <p>Falta <b className="num">{formatMoney(reservationRest(mode.r))} MT</b>. Quanto paga agora?</p>
          <div className="flex items-center gap-2">
            <input autoFocus inputMode="decimal" aria-label="Valor a pagar" value={mode.amount} onChange={(e) => setMode({ ...mode, amount: e.target.value })} className="num h-14 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-2xl" />
            <span className="text-xl font-bold">MT</span>
          </div>
          <button className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">Continuar para o pagamento</button>
        </form>
      )}
      {mode?.kind === 'collect' && (
        <div className="space-y-4">
          <p>Já está tudo pago ({formatMoney(reservationPaid(mode.r))} MT). Entregue as peças ao cliente.</p>
          <button disabled={busy} onClick={() => void run(async () => {
            const r = await collectReservation(p.store, who, p.signer, { reservation: mode.r, tenders: [], operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence });
            await keep({ ...mode.r, status: 'collected' });
            p.onDone([r.document, ...r.creditNotes], `Reserva ${mode.r.code} levantada`);
          })} className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Levantar e emitir a factura</button>
        </div>
      )}
      {mode?.kind === 'cancel' && (
        !mode.approver ? (
          <ManagerApproval store={p.store} operator={p.operator} why="Anular uma reserva precisa de um gerente" onApproved={(m) => setMode({ ...mode, approver: m })} />
        ) : (
          <form className="space-y-4" onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const refund = mode.refund === 'none' ? null : { method: mode.refund, ...(mode.refund === 'MP' ? { reference: mode.ref.trim() } : {}) };
              const r = await cancelReservation(p.store, who, p.signer, { reservation: mode.r, refund, reason: mode.reason, approvedBy: mode.approver!.id, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence });
              await keep({ ...mode.r, status: 'cancelled' });
              p.onDone(r.creditNotes, mode.refund === 'none' ? `Reserva ${mode.r.code} anulada; o sinal fica` : `Reserva ${mode.r.code} anulada: devolver ${formatMoney(reservationPaid(mode.r))} MT`);
            });
          }}>
            <p>Pago: <b className="num">{formatMoney(reservationPaid(mode.r))} MT</b>. Autorizado por {mode.approver.name}.</p>
            <div className="grid grid-cols-3 gap-2">
              {([['NU', 'Devolver em dinheiro'], ['MP', 'Devolver por M-Pesa'], ['none', 'O sinal fica']] as const).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={mode.refund === k} onClick={() => setMode({ ...mode, refund: k })} className={clsx('min-h-12 rounded-xl border-2 px-2 text-sm font-semibold', mode.refund === k ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{l}</button>
              ))}
            </div>
            {mode.refund === 'MP' && <input aria-label="Referência do M-Pesa" placeholder="Referência da transacção" value={mode.ref} onChange={(e) => setMode({ ...mode, ref: e.target.value })} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />}
            {mode.refund === 'none' && <p className="text-sm text-muted">Só se o cliente desistiu e ficou combinado que perde o sinal. As facturas de adiantamento ficam como estão.</p>}
            <input aria-label="Motivo" placeholder="Motivo (ex.: desistiu, não levantou no prazo)" value={mode.reason} onChange={(e) => setMode({ ...mode, reason: e.target.value })} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
            <button disabled={busy || mode.reason.trim().length < 3 || (mode.refund === 'MP' && !mode.ref.trim())} className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Anular a reserva</button>
          </form>
        )
      )}
    </Shell>
  );
}
